import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { DateRangePicker, MultipleSelect, Select } from 'components/FormComponents';
import { useTableContext } from 'components/Table/Table';
import tableStyles from 'components/Table/Table.module.scss';

const DATE_FIELDS = [
  { value: 'created_at', name: 'Created' },
  { value: 'submitted_at', name: 'Submitted' },
  { value: 'completed_at', name: 'Completed' },
  { value: 'deferred_until', name: 'Deferred until' },
  { value: 'next_action_at', name: 'Next action' },
  { value: 'sub_status_last_updated_at', name: 'Sub-status updated' },
];

const IN_PROGRESS_OPTIONS = [
  { value: 'processing', name: 'Processing' },
  { value: 'waiting_on_candidate', name: 'Waiting on Candidate' },
  { value: 'waiting_on_provider', name: 'Waiting on Provider' },
  { value: 'waiting_on_client', name: 'Waiting on Client' },
  { value: 'blocked', name: 'Blocked' },
];

const EMAIL_ISSUE_FLAGS = ['candidate_email_failed', 'provider_email_failed'];

const ensureArray = value =>
  Array.isArray(value) ? value : value != null && value !== '' ? [value] : [];

const sameList = (left, right) =>
  left.length === right.length && left.every((item, index) => item === right[index]);

const rangeValue = param => {
  const value = param?.value;
  return Array.isArray(value) && value.length === 2 ? value : null;
};

export const AdminChecksDateFilter = () => {
  const { queryParams, updateQueryParams } = useTableContext();
  const activeField = DATE_FIELDS.find(field => rangeValue(queryParams[field.value]));
  const [field, setField] = useState(activeField?.value || '');
  const [range, setRange] = useState(
    activeField ? rangeValue(queryParams[activeField.value]) : null,
  );

  useEffect(() => {
    const nextField = DATE_FIELDS.find(item => rangeValue(queryParams[item.value]));
    if (!nextField) {
      setRange(null);
      return;
    }
    setField(nextField.value);
    setRange(rangeValue(queryParams[nextField.value]));
  }, [queryParams]);

  const write = (nextField, nextRange) => {
    const update = { page: { value: 1 } };
    DATE_FIELDS.forEach(item => {
      const selected = item.value === nextField && nextRange;
      update[item.value] = selected
        ? { title: item.name, name: item.name, value: nextRange, type: 'date_range' }
        : null;
    });
    updateQueryParams(update);
  };

  return (
    <>
      <Select
        className={tableStyles.filter}
        value={field}
        useDefault={false}
        placeholder="Date Type"
        inputProps={{
          onChange: event => {
            const nextField = event.target.value;
            setField(nextField);
            if (range) write(nextField, range);
          },
        }}>
        {DATE_FIELDS.map(item => (
          <Select.Item key={item.value} value={item.value}>
            {item.name}
          </Select.Item>
        ))}
      </Select>
      <div className={tableStyles.filter}>
        <DateRangePicker
          value={range || undefined}
          placeholder="Date Range"
          onChange={nextRange => {
            const nextField = field || 'created_at';
            setField(nextField);
            setRange(nextRange);
            write(nextField, nextRange);
          }}
          outputFormat="YYYY-MM-DD"
        />
      </div>
    </>
  );
};

export const AdminChecksStatusFilter = () => {
  const { t } = useTranslation();
  const { queryParams, updateQueryParams } = useTableContext();
  const plainOptions = [
    { value: 'submitted', name: t('common.submitted') },
    { value: 'deferred', name: t('common.deferred') },
    { value: 'completed', name: t('common.completed') },
    { value: 'candidate', name: t('common.candidate') },
    { value: 'canceled', name: t('common.canceled') },
  ];
  const options = [plainOptions[0], ...IN_PROGRESS_OPTIONS, ...plainOptions.slice(1)].map(
    option => ({
      ...option,
      token: IN_PROGRESS_OPTIONS.some(sub => sub.value === option.value)
        ? `sub:${option.value}`
        : `status:${option.value}`,
    }),
  );

  const tokensFromQuery = () => {
    const statuses = ensureArray(queryParams.status?.value);
    const subs = ensureArray(queryParams.in_progress_sub_status?.value);
    const tokens = statuses
      .filter(value => value !== 'in-progress')
      .map(value => `status:${value}`);
    if (subs.length) {
      subs.forEach(value => tokens.push(`sub:${value}`));
    } else if (statuses.includes('in-progress')) {
      IN_PROGRESS_OPTIONS.forEach(option => tokens.push(`sub:${option.value}`));
    }
    return tokens;
  };

  const [selected, setSelected] = useState(tokensFromQuery);

  useEffect(() => {
    const next = tokensFromQuery();
    setSelected(current => (sameList(current, next) ? current : next));
  }, [queryParams.status, queryParams.in_progress_sub_status]);

  const onChange = tokens => {
    setSelected(tokens);
    const plain = [];
    const subs = [];
    tokens.forEach(token => {
      if (token.startsWith('sub:')) subs.push(token.slice(4));
      if (token.startsWith('status:')) plain.push(token.slice(7));
    });
    const statusValues = subs.length ? [...plain, 'in-progress'] : plain;
    updateQueryParams({
      status: {
        title: 'Status',
        type: 'status',
        value: statusValues,
        name: statusValues.map(value =>
          value === 'in-progress' ? '' : plainOptions.find(option => option.value === value)?.name,
        ),
      },
      in_progress_sub_status: {
        title: 'In-progress status',
        type: 'status',
        value: subs,
        name: subs.map(value => IN_PROGRESS_OPTIONS.find(option => option.value === value)?.name),
      },
      page: { value: 1 },
    });
  };

  return (
    <MultipleSelect
      className={tableStyles.filter}
      value={selected}
      onChange={onChange}
      placeholder="Status"
      hideLabel>
      {options.map(option => (
        <MultipleSelect.Item
          key={option.token}
          value={option.token}
          active={selected.includes(option.token)}>
          {option.name}
        </MultipleSelect.Item>
      ))}
    </MultipleSelect>
  );
};

const statusParam = (title, values, options) => ({
  title,
  type: 'status',
  value: values,
  name: values.map(value => options.find(option => option.value === value)?.name || value),
});

const ChoiceFilter = ({ title, paramName, options, clearedParams = [], onWrite }) => {
  const { queryParams, updateQueryParams } = useTableContext();

  const selectedFromQuery = () => {
    const direct = ensureArray(queryParams[paramName]?.value).map(String);
    const fromExisting = options
      .filter(option =>
        option.sources.some(source =>
          ensureArray(queryParams[source.key]?.value).map(String).includes(source.value),
        ),
      )
      .map(option => option.value);
    const known = new Set(options.map(option => option.value));
    return [...new Set([...direct, ...fromExisting])].filter(value => known.has(value));
  };

  const [selected, setSelected] = useState(selectedFromQuery);

  useEffect(() => {
    const next = selectedFromQuery();
    setSelected(current => (sameList(current, next) ? current : next));
  }, [queryParams]);

  const onChange = values => {
    setSelected(values);
    const update = onWrite
      ? onWrite(values, queryParams)
      : {
          [paramName]: statusParam(title, values, options),
          ...Object.fromEntries(
            clearedParams.map(key => [key, { title: key, type: 'status', value: [], name: [] }]),
          ),
        };
    updateQueryParams({ ...update, page: { value: 1 } });
  };

  return (
    <MultipleSelect
      className={tableStyles.filter}
      value={selected}
      onChange={onChange}
      placeholder={title}
      hideLabel>
      {options.map(option => (
        <MultipleSelect.Item
          key={option.value}
          value={option.value}
          active={selected.includes(option.value)}>
          {option.name}
        </MultipleSelect.Item>
      ))}
    </MultipleSelect>
  );
};

const ISSUE_OPTIONS = [
  {
    value: 'id_not_met',
    name: 'ID requirements not met',
    sources: [{ key: 'id_requirements_met', value: 'false' }],
  },
  { value: 'failed', name: 'Bot failed', sources: [{ key: 'bot_status', value: 'failed' }] },
  {
    value: 'candidate_email_failed',
    name: 'Candidate email failed',
    sources: [{ key: 'support_flags', value: 'candidate_email_failed' }],
  },
  {
    value: 'provider_email_failed',
    name: 'Provider email failed',
    sources: [{ key: 'support_flags', value: 'provider_email_failed' }],
  },
];

const HELP_OPTIONS = [
  { value: 'idv_help', name: 'IDV help', sources: [{ key: 'support_flags', value: 'idv_help' }] },
  {
    value: 'manual_process',
    name: 'Manual process',
    sources: [{ key: 'support_flags', value: 'manual_process' }],
  },
  {
    value: 'outbound_call',
    name: 'Outbound call',
    sources: [{ key: 'support_flags', value: 'outbound_call' }],
  },
  {
    value: 'image_help',
    name: 'Image help',
    sources: [{ key: 'support_flags', value: 'image_help' }],
  },
];

const AUDIT_OPTIONS = [
  {
    value: 'document_request',
    name: 'Document request',
    sources: [{ key: 'open_activity', value: 'document_request' }],
  },
  {
    value: 'no_upload',
    name: 'Missing result file',
    sources: [{ key: 'upload_id_status', value: 'no_upload' }],
  },
  {
    value: 'unassigned',
    name: 'Unassigned',
    sources: [],
  },
];

const withoutValues = (param, remove) =>
  ensureArray(param?.value)
    .map(String)
    .filter(value => !remove.includes(value));

export const AdminChecksWorkFilter = ({ overdueSupportHours, showSubchecks = false }) => (
  <ChoiceFilter
    title="Prioritize"
    paramName="work"
    options={[
      {
        value: 'intercom',
        name: 'Intercom reply',
        sources: [{ key: 'open_activity', value: 'intercom' }],
      },
      {
        value: 'due',
        name: 'Next action due',
        sources: [{ key: 'next_action_due', value: 'due' }],
      },
      ...(!showSubchecks
        ? [
            {
              value: 'overdue_provider',
              name: 'Overdue provider',
              sources: [{ key: 'overdue_status', value: 'overdue_provider' }],
            },
          ]
        : []),
      {
        value: 'overdue_support',
        name: `Overdue support (${overdueSupportHours}h)`,
        sources: [{ key: 'overdue_status', value: 'overdue_support' }],
      },
    ]}
    onWrite={(values, queryParams) => ({
      work: statusParam('Prioritize', values, [
        { value: 'intercom', name: 'Intercom reply' },
        { value: 'due', name: 'Next action due' },
        { value: 'overdue_provider', name: 'Overdue provider' },
        { value: 'overdue_support', name: `Overdue support (${overdueSupportHours}h)` },
      ]),
      next_action_due: { title: 'Next action due', type: 'status', value: [], name: [] },
      overdue_status: { title: 'Overdue', type: 'status', value: [], name: [] },
      open_activity: statusParam(
        'Open activity',
        withoutValues(queryParams.open_activity, ['intercom']),
        [{ value: 'document_request', name: 'Document request' }],
      ),
    })}
  />
);

export const AdminChecksIssuesFilter = ({ showSubchecks = false }) => (
  <ChoiceFilter
    title="Issues"
    paramName="issues"
    options={ISSUE_OPTIONS.filter(option => !(showSubchecks && option.value === 'id_not_met'))}
    onWrite={(values, queryParams) => ({
      issues: statusParam('Issues', values, ISSUE_OPTIONS),
      bot_status: { title: 'Bot failed', type: 'status', value: [], name: [] },
      id_requirements_met: { title: 'ID requirements', type: 'status', value: [], name: [] },
      support_flags: statusParam(
        'Help Required',
        withoutValues(queryParams.support_flags, EMAIL_ISSUE_FLAGS),
        HELP_OPTIONS,
      ),
    })}
  />
);

export const AdminChecksHelpFilter = () => (
  <ChoiceFilter
    title="Help Required"
    paramName="help"
    options={HELP_OPTIONS}
    onWrite={(values, queryParams) => {
      const carriedIssues = [
        ...new Set([
          ...ensureArray(queryParams.issues?.value).map(String),
          ...ensureArray(queryParams.support_flags?.value)
            .map(String)
            .filter(value => EMAIL_ISSUE_FLAGS.includes(value)),
        ]),
      ];
      return {
        help: { title: 'Help Required', type: 'status', value: [], name: [] },
        support_flags: statusParam('Help Required', values, HELP_OPTIONS),
        issues: statusParam('Issues', carriedIssues, ISSUE_OPTIONS),
      };
    }}
  />
);

export const AdminChecksAuditFilter = () => (
  <ChoiceFilter
    title="Audit"
    paramName="audit"
    options={AUDIT_OPTIONS}
    onWrite={(values, queryParams) => ({
      audit: statusParam('Audit', values, AUDIT_OPTIONS),
      upload_id_status: { title: 'Missing result file', type: 'status', value: [], name: [] },
      open_activity: statusParam(
        'Open activity',
        withoutValues(queryParams.open_activity, ['document_request']),
        [{ value: 'intercom', name: 'Intercom reply' }],
      ),
    })}
  />
);
